import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const CONFIRM_TIMEOUT_MS = 4000;

/**
 * A custom element that empties the entire cart.
 *
 * Uses an inline two-tap confirmation (tap once to arm, tap again to confirm)
 * instead of window.confirm(): native confirm() is unreliable on some mobile
 * browsers (doesn't appear at all in some contexts, e.g. inside an already
 * open native <dialog>), while this works identically everywhere since it's
 * plain DOM state we control on the button itself.
 *
 * @typedef {Object} CartClearComponentRefs
 * @property {HTMLButtonElement} clearCartButton - The "vaciar carrito" button.
 *
 * @extends {Component<CartClearComponentRefs>}
 */
class CartClear extends Component {
  requiredRefs = ['clearCartButton'];

  /** @type {boolean} */
  #armed = false;

  /** @type {string} */
  #originalLabel = '';

  /** @type {number | undefined} */
  #resetTimeout;

  /**
   * Handles taps on the "vaciar carrito" button.
   * First tap arms the confirmation state; second tap (within
   * CONFIRM_TIMEOUT_MS) empties the cart via /cart/clear.js and morphs the
   * cart-summary section. cart-items-component picks up the resulting
   * CartUpdateEvent on its own (same path already used for
   * DiscountUpdateEvent), so we never touch the DOM outside this section
   * directly and never reload the page.
   * @param {MouseEvent} event
   */
  clearCart = async (event) => {
    event.preventDefault();

    const { clearCartButton } = this.refs;

    if (!this.#armed) {
      this.#arm(clearCartButton);
      return;
    }

    this.#disarm(clearCartButton);

    if (typeof this.dataset.sectionId !== 'string') return;

    clearCartButton.disabled = true;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ sections: [this.dataset.sectionId] }),
      });

      // Theme.routes no expone cart_clear_url en este tema (a diferencia de
      // cart_change_url / cart_update_url), así que usamos el endpoint
      // estándar de la Cart AJAX API de Shopify directamente.
      const response = await fetch('/cart/clear.js', config);
      const data = await response.json();

      document.dispatchEvent(
        new CartUpdateEvent(data, this.id, {
          itemCount: 0,
          source: 'cart-clear-component',
          sections: data.sections,
        })
      );

      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
      console.error(error);
      clearCartButton.disabled = false;
    }
  };

  /**
   * @param {HTMLButtonElement} button
   */
  #arm(button) {
    this.#armed = true;
    this.#originalLabel = button.textContent ?? '';
    button.textContent = '¿Seguro? Toca de nuevo para vaciar';
    button.classList.add('cart-clear__button--confirming');

    this.#resetTimeout = window.setTimeout(() => this.#disarm(button), CONFIRM_TIMEOUT_MS);
  }

  /**
   * @param {HTMLButtonElement} button
   */
  #disarm(button) {
    clearTimeout(this.#resetTimeout);
    this.#armed = false;
    button.textContent = this.#originalLabel;
    button.classList.remove('cart-clear__button--confirming');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#resetTimeout);
  }
}

if (!customElements.get('cart-clear-component')) {
  customElements.define('cart-clear-component', CartClear);
}
